import { useEffect, useRef, useState } from 'react';
import { Check, ListFilter, Search, X } from 'lucide-react';
import type { AgentIdentity } from '../utils/agentLabels';
import { ALL_AGENTS, agentDisplayName, agentInitial, agentTone } from '../utils/agentLabels';
import type { UnifiedTaskView } from '../utils/unifiedTasks';
import styles from './TaskListHeader.module.css';

const TONE_CLASS = {
  accent: styles.toneAccent,
  info: styles.toneInfo,
  purple: styles.tonePurple,
  neutral: styles.toneNeutral,
} as const;

/**
 * The task list's header in the conversation view (the default): "Tasks" with a search button and one
 * Filter button, then the view switch. Agent and "Updated today" live in the Filter menu; when
 * either is on, a "Showing …" line names it with a way to remove it, so a short list is never a
 * mystery (the agent filter is remembered between visits). Search opens in place.
 */
export function TaskListHeader({
  views,
  view,
  counts,
  onView,
  search,
  onSearch,
  agents,
  agentTypes,
  agentFilter,
  agentCounts,
  onAgent,
  updatedToday,
  onToggleToday,
}: {
  views: ReadonlyArray<{ value: UnifiedTaskView; label: string }>;
  view: UnifiedTaskView;
  counts: Record<UnifiedTaskView, number>;
  onView: (view: UnifiedTaskView) => void;
  search: string;
  onSearch: (value: string) => void;
  agents: readonly AgentIdentity[];
  agentTypes: readonly string[];
  /** The selected agent's role key, or ALL_AGENTS. */
  agentFilter: string;
  /** Tasks in the current view per agent role key, plus ALL_AGENTS for every agent. */
  agentCounts: Record<string, number>;
  onAgent: (key: string) => void;
  updatedToday: boolean;
  onToggleToday: () => void;
}) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const agentOn = agentFilter !== ALL_AGENTS;
  const activeCount = (agentOn ? 1 : 0) + (updatedToday ? 1 : 0);
  const showSearch = searchOpen || search.length > 0;

  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  // The menu closes on a click outside it or Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const clearFilters = () => {
    if (agentOn) onAgent(ALL_AGENTS);
    if (updatedToday) onToggleToday();
  };
  const closeSearch = () => {
    onSearch('');
    setSearchOpen(false);
  };

  return (
    <div className={styles.header}>
      <div className={styles.titleRow}>
        <h1 className={styles.title}>Tasks</h1>
        <button
          type="button"
          className={showSearch ? `${styles.iconButton} ${styles.buttonOn}` : styles.iconButton}
          onClick={() => (showSearch ? closeSearch() : setSearchOpen(true))}
          aria-label={showSearch ? 'Close search' : 'Search tasks'}
          aria-expanded={showSearch}
        >
          <Search size={15} aria-hidden />
        </button>
        <div className={styles.menuAnchor} ref={menuRef}>
          <button
            type="button"
            className={
              menuOpen || activeCount > 0
                ? `${styles.filterButton} ${styles.buttonOn}`
                : styles.filterButton
            }
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-haspopup="dialog"
          >
            <ListFilter size={14} aria-hidden />
            Filter
            {activeCount > 0 && <span className={styles.badge}>{activeCount}</span>}
          </button>
          {menuOpen && (
            <div className={styles.menu} role="dialog" aria-label="Filter tasks">
              <div className={styles.menuLabel}>Agent</div>
              <div className={styles.menuList} role="radiogroup" aria-label="Agent">
                {[ALL_AGENTS, ...agentTypes].map((key) => {
                  const selected = agentFilter === key;
                  const name = key === ALL_AGENTS ? 'All agents' : agentDisplayName(key, agents);
                  return (
                    <button
                      key={key}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      className={
                        selected ? `${styles.menuItem} ${styles.menuItemOn}` : styles.menuItem
                      }
                      onClick={() => onAgent(key)}
                    >
                      <span className={styles.check}>
                        {selected && <Check size={14} aria-hidden />}
                      </span>
                      {key !== ALL_AGENTS && (
                        <span className={`${styles.avatar} ${TONE_CLASS[agentTone(key)]}`}>
                          {agentInitial(name)}
                        </span>
                      )}
                      <span className={styles.menuName}>{name}</span>
                      <span className={styles.menuCount}>{agentCounts[key] ?? 0}</span>
                    </button>
                  );
                })}
              </div>
              <div className={styles.menuDivider} />
              <div className={styles.menuLabel}>When</div>
              <label className={styles.menuCheck}>
                <input type="checkbox" checked={updatedToday} onChange={onToggleToday} />
                Updated today only
              </label>
              <div className={styles.menuFooter}>
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={clearFilters}
                  disabled={activeCount === 0}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className={styles.doneButton}
                  onClick={() => setMenuOpen(false)}
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showSearch && (
        <div className={styles.searchField}>
          <Search className={styles.searchIcon} size={15} aria-hidden />
          <input
            ref={searchRef}
            className={styles.searchInput}
            type="search"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') closeSearch();
            }}
            placeholder="Search tasks and types"
            aria-label="Search tasks and types"
          />
        </div>
      )}

      <nav className={styles.segmented} aria-label="Task views">
        {views.map((option) => {
          const active = view === option.value;
          return (
            <button
              key={option.value}
              type="button"
              className={active ? `${styles.segment} ${styles.segmentOn}` : styles.segment}
              onClick={() => onView(option.value)}
              aria-pressed={active}
            >
              {option.label}
              <span className={styles.segmentCount}>{counts[option.value]}</span>
            </button>
          );
        })}
      </nav>

      {activeCount > 0 && (
        <div className={styles.showing} aria-live="polite">
          <span>Showing</span>
          {agentOn && (
            <span className={styles.chip}>
              {agentDisplayName(agentFilter, agents)}
              <button
                type="button"
                className={styles.chipRemove}
                onClick={() => onAgent(ALL_AGENTS)}
                aria-label={`Remove ${agentDisplayName(agentFilter, agents)} filter`}
              >
                <X size={12} aria-hidden />
              </button>
            </span>
          )}
          {updatedToday && (
            <span className={styles.chip}>
              Updated today
              <button
                type="button"
                className={styles.chipRemove}
                onClick={onToggleToday}
                aria-label="Remove updated today filter"
              >
                <X size={12} aria-hidden />
              </button>
            </span>
          )}
          <button type="button" className={styles.linkButton} onClick={clearFilters}>
            Clear
          </button>
        </div>
      )}
    </div>
  );
}
